import React, { useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'semantic-ui-react';
import { useSelector, useDispatch } from 'react-redux';
import { UserContext } from 'src/context';
import { Roles } from 'src/utils/roles';
import { useBitacoras, useDeleteBitacora } from 'src/graphql/bitacora';
import { importAlertState } from 'src/redux/actions/alert';
import { importOpenSidebar } from 'src/redux/actions/openSidebar';
import BitacoraListItem from './BitacoraListItem';
import BitacoraDetailView from './BitacoraDetailView';
import BitacoraModal from './BitacoraModal';
import BitacoraNotificationModal from './BitacoraNotificationModal';
import BitacoraSkeleton from './BitacoraSkeleton';
import CustomModal from 'src/components/configuracion/components/customModal';
import FeatherIcon from 'feather-icons-react';
import './bitacora.scss';

const canViewDraft = (roles) => roles?.includes(Roles.ADMIN) || roles?.includes(Roles.SUPER_COLABORADOR) || roles?.includes(Roles.COLABORADOR);
const canCreate = (roles) => roles?.includes(Roles.ADMIN) || roles?.includes(Roles.SUPER_COLABORADOR) || roles?.includes(Roles.COLABORADOR);
const canNotify = (roles) => roles?.includes(Roles.ADMIN) || roles?.includes(Roles.SUPER_COLABORADOR);
const PAGE_SIZE = 20;

const BitacoraPanel = ({ closeMenu, companyId, isExpanded = false, onToggleExpand, initialExpandedBitacoraId }) => {
  const { t } = useTranslation('finance');
  const dispatch = useDispatch();
  const { roles } = useContext(UserContext);
  const { selectedCompany } = useSelector((state) => state.companyList);
  const { openSidebar } = useSelector((state) => state.openSidebar);

  const effectiveCompanyId = companyId || selectedCompany?.id;
  const canViewDrafts = canViewDraft(roles);
  const canCreateBitacora = canCreate(roles);
  const canNotifyUser = canNotify(roles);

  const { data, fetching, error, reexecuteQuery } = useBitacoras(effectiveCompanyId);
  const { executeMutation: deleteBitacora, fetching: deleting } = useDeleteBitacora();

  // No re-ejecutar en useEffect: useQuery ya hace la petición al montar y al cambiar companyId.
  // reexecuteQuery solo se usa en handleRefresh (tras crear/editar/eliminar/enviar) para evitar
  // múltiples llamadas que pueden saturar el servidor.

  const prevErrorRef = React.useRef(null);
  useEffect(() => {
    if (error && error !== prevErrorRef.current) {
      prevErrorRef.current = error;
      const message = error?.message || error?.graphQLErrors?.[0]?.message || 'Error al cargar bitácoras';
      dispatch(importAlertState({ content: message, state: 'error', duration: 5 }));
    }
    if (!error) prevErrorRef.current = null;
  }, [error, dispatch]);

  const bitacoras = Array.isArray(data?.bitacoras) ? data.bitacoras : [];

  const filteredBitacoras = canViewDrafts
    ? bitacoras
    : bitacoras.filter((b) => b && b.status === 'delivered');

  const sortedBitacoras = [...filteredBitacoras].sort((a, b) => {
    const dateA = new Date(a.flowDate || a.createdAt);
    const dateB = new Date(b.flowDate || b.createdAt);
    return dateB - dateA;
  });

  const [expandedBitacoraId, setExpandedBitacoraId] = React.useState(initialExpandedBitacoraId ?? null);
  const detailViewRef = React.useRef(null);

  React.useEffect(() => {
    if (initialExpandedBitacoraId != null) {
      setExpandedBitacoraId(initialExpandedBitacoraId);
    }
  }, [initialExpandedBitacoraId]);
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [showNotificationModal, setShowNotificationModal] = React.useState(false);
  const [bitacoraToSend, setBitacoraToSend] = React.useState(null);
  const [bitacoraToDelete, setBitacoraToDelete] = React.useState(null);
  const [page, setPage] = React.useState(1);

  const handleRefresh = () => {
    reexecuteQuery();
  };

  const handleCreateClick = () => {
    setShowCreateModal(true);
    setExpandedBitacoraId(null);
  };

  const handleBitacoraViewToggle = (bitacora) => {
    setExpandedBitacoraId((prev) => (prev === bitacora?.id ? null : bitacora?.id ?? null));
  };

  const handleEditClick = (bitacora) => {
    if (bitacora?.status === 'draft') {
      setExpandedBitacoraId((prev) => (prev === bitacora?.id ? prev : bitacora?.id ?? null));
    }
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
    handleRefresh();
  };

  const handleSendClick = (bitacora) => {
    setBitacoraToSend(bitacora);
    setShowNotificationModal(true);
  };

  const handleNotificationConfirm = async () => {
    setShowNotificationModal(false);
    setBitacoraToSend(null);
    handleRefresh();
  };

  const handleHistory = (bitacora) => {
    const base = openSidebar || {};
    dispatch(
      importOpenSidebar({
        ...base,
        show: true,
        historyView: true,
        bitacora: false,
        editData: { bitacora },
        options: { ...(base.options || {}), type: 'bitacora' },
        companyId: effectiveCompanyId,
      })
    );
  };

  const handleDeleteBitacora = async (bitacora) => {
    if (!bitacora?.id || bitacora?.status !== 'draft') return;
    const { error: err } = await deleteBitacora(bitacora.id);
    if (err) {
      const message = err?.message || err?.graphQLErrors?.[0]?.message || 'Error al eliminar';
      dispatch(importAlertState({ content: message, state: 'error', duration: 5 }));
    } else {
      setBitacoraToDelete(null);
      handleRefresh();
    }
  };

  const handleConfirmDeleteDraft = async () => {
    if (bitacoraToDelete) await handleDeleteBitacora(bitacoraToDelete);
  };

  const totalBitacoras = sortedBitacoras.length;
  const totalPages = Math.max(1, Math.ceil(totalBitacoras / PAGE_SIZE));
  const pageStart = (page - 1) * PAGE_SIZE;
  const paginatedBitacoras = sortedBitacoras.slice(pageStart, pageStart + PAGE_SIZE);

  React.useEffect(() => {
    if (page > totalPages && totalPages > 0) setPage(1);
  }, [page, totalPages]);

  const emptyMessage = canViewDrafts
    ? bitacoras.length === 0
      ? t('bitacora.noDeliveries')
      : bitacoras.every((b) => b.status === 'draft')
        ? t('bitacora.notSentYet')
        : null
    : t('bitacora.noDeliveries');

  return (
    <div className="bitacora-panel">
      <div className="bitacora-panel-header">
        <div className="bitacora-panel-header-left">
          <div className="bitacora-panel-header-title-wrap">
            <h2 className="bitacora-panel-title">{t('bitacora.title')}</h2>
            <div className="bitacora-panel-sub">
              {t('bitacora.companyDocuments', {
                company: selectedCompany?.name || selectedCompany?.alias || 'la empresa',
              })}
            </div>
          </div>
        </div>
        <div className="bitacora-panel-header-center">
          {canCreateBitacora && (
            <Button
              size="small"
              onClick={handleCreateClick}
              className="bitacora-create-btn"
            >
              <span className="bitacora-create-btn-inner">
                <FeatherIcon icon="plus" size={18} strokeWidth={2.5} className="bitacora-create-btn-plus-icon" />
                <span>{t('bitacora.create')}</span>
              </span>
            </Button>
          )}
        </div>
        <div className="bitacora-panel-header-right">
          {onToggleExpand && (
            <button
              type="button"
              onClick={onToggleExpand}
              className="bitacora-panel-expand-btn"
              title={isExpanded ? 'Achicar pantalla' : 'Expandir pantalla'}
            >
              <FeatherIcon
                icon={isExpanded ? 'minimize-2' : 'maximize-2'}
                size={20}
                strokeWidth={2}
              />
            </button>
          )}
          {closeMenu && (
            <button
              type="button"
              onClick={closeMenu}
              className="bitacora-panel-close-btn"
              title="Cerrar barra"
            >
              <FeatherIcon icon="x" size={20} strokeWidth={2} />
            </button>
          )}
        </div>
      </div>

      <div className="bitacora-panel-content">
        {fetching && sortedBitacoras.length === 0 ? (
          <BitacoraSkeleton count={5} />
        ) : sortedBitacoras.length === 0 ? (
          <div className="bitacora-empty-state">
            <p>{emptyMessage}</p>
          </div>
        ) : (
          <div className="bitacora-list">
            {paginatedBitacoras.length === 0 ? (
              <div className="bitacora-empty-state">
                <p>No hay bitácoras.</p>
              </div>
            ) : (
              <>
                {paginatedBitacoras.map((bitacora) => {
                  const isExpanded = expandedBitacoraId === bitacora.id;
                  return (
                    <BitacoraListItem
                      key={bitacora.id}
                      bitacora={bitacora}
                      isExpanded={isExpanded}
                      onView={() => handleBitacoraViewToggle(bitacora)}
                      onEdit={handleEditClick}
                      onSave={() => detailViewRef.current?.save?.()}
                      onNotify={handleSendClick}
                      onDelete={(bitacora) => setBitacoraToDelete(bitacora)}
                      canNotify={canNotifyUser}
                    >
                      {isExpanded && (
                        <BitacoraDetailView
                          ref={detailViewRef}
                          bitacora={bitacora}
                          companyId={effectiveCompanyId}
                          canCreate={canCreateBitacora}
                          onBack={() => setExpandedBitacoraId(null)}
                          onSend={handleSendClick}
                          onNotify={handleSendClick}
                          onHistory={handleHistory}
                          onRefresh={handleRefresh}
                          embedded
                          canNotify={canNotifyUser}
                        />
                      )}
                    </BitacoraListItem>
                  );
                })}
                {totalPages > 1 && (
                  <div className="bitacora-pagination">
                    <button
                      type="button"
                      className="bitacora-pagination-btn"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Anterior
                    </button>
                    <span className="bitacora-pagination-info">
                      {pageStart + 1}-{Math.min(pageStart + PAGE_SIZE, totalBitacoras)} de {totalBitacoras}
                    </span>
                    <button
                      type="button"
                      className="bitacora-pagination-btn"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Siguiente
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <BitacoraModal
        open={showCreateModal}
        onClose={handleModalClose}
        bitacora={null}
        companyId={effectiveCompanyId}
        onSuccess={handleModalClose}
        onEnviarClick={(b) => {
          setBitacoraToSend(b);
          setShowNotificationModal(true);
          setShowCreateModal(false);
          handleRefresh();
        }}
      />

      <BitacoraNotificationModal
        open={showNotificationModal}
        onClose={() => {
          setShowNotificationModal(false);
          setBitacoraToSend(null);
        }}
        bitacora={bitacoraToSend}
        companyId={effectiveCompanyId}
        onConfirm={handleNotificationConfirm}
      />

      <CustomModal
        open={!!bitacoraToDelete}
        onClose={() => {
          if (!deleting) setBitacoraToDelete(null);
        }}
        title="Eliminar borrador"
        content={
          <div>
            <p>¿Estás seguro de que quieres eliminar este borrador?</p>
            <p className="tw-mt-2 tw-text-gray-600 tw-text-sm">Esta acción es irreversible y no se puede deshacer.</p>
          </div>
        }
        onConfirm={handleConfirmDeleteDraft}
        confirmLabel="Sí, eliminar"
        confirmDisabled={deleting}
        isLoading={deleting}
        cancelarLabel="Cancelar"
        size="tiny"
        classNameTitle="!tw-text-center !tw-text-[16px] !tw-font-semibold"
      />

    </div>
  );
};

export default BitacoraPanel;
